<template>
  <div>
    <Navbar />

    <main>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Page Hero                                                     -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="page-hero">
        <div class="page-hero-overlay"></div>
        <div class="page-container">
          <div class="page-hero-content">
            <div class="hero-eyebrow">Our Story</div>
            <h1 class="page-hero-title">Built to Simplify How<br />Businesses Operate</h1>
            <p class="page-hero-sub">
              Mapiibs is a fully integrated business management platform covering every department —
              from inventory and sales to accounting, payroll, HR and beyond.
            </p>
            <nav class="breadcrumb-nav" aria-label="Breadcrumb">
              <router-link to="/home" class="breadcrumb-link">Home</router-link>
              <span class="breadcrumb-sep">/</span>
              <span class="breadcrumb-current">About Us</span>
            </nav>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Mission strip                                                 -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="mission-strip">
        <div class="page-container">
          <div class="mission-inner">
            <font-awesome-icon icon="lightbulb" class="mission-icon" />
            <p class="mission-text">
              Our mission is to give every business — regardless of size — access to the same
              powerful tools that were once reserved for large enterprises, at a price that makes sense.
            </p>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Who We Are                                                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="split-grid">
            <div class="split-content">
              <p class="section-eyebrow">Who We Are</p>
              <h2 class="section-title">
                <span class="accent">Mapiibs</span>: Intelligent Business Management for the Modern Era
              </h2>
              <div class="section-body" v-html="details.short_description || 'Mapiibs is a Modular Accounting, Payroll, Inventory, Invoicing and Billing System that brings your entire business into one unified platform. We eliminate the friction between disconnected tools and give your team a single source of truth.'"></div>
              <ul class="check-list">
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Unified accounting, invoicing &amp; billing</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Complete HR, payroll &amp; loan management</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Real-time inventory across multiple locations</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Sales, purchases, debtors &amp; creditors</li>
                <li><font-awesome-icon icon="check-circle" class="check-icon" /> Comprehensive reports &amp; analytics suite</li>
              </ul>
              <router-link to="/auth/register" class="btn-primary-md">Get Started Free</router-link>
            </div>
            <div class="split-visual">
              <img src="/static/img/cat-v2-7.svg" alt="Mapiibs platform" />
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Stats                                                         -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="stats-section">
        <div class="page-container">
          <div class="stats-grid">
            <div v-for="stat in stats" :key="stat.label" class="stat-card">
              <div class="stat-icon-wrap" :style="{ background: stat.bg, color: stat.color }">
                <font-awesome-icon :icon="stat.icon" />
              </div>
              <div class="stat-number">{{ stat.number }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Our Platform — all modules                                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray">
        <div class="page-container">
          <div class="section-header">
            <p class="section-eyebrow">Our Platform</p>
            <h2 class="section-title">13 Modules. One Seamlessly Integrated System.</h2>
            <p class="section-body">
              Every module in Mapiibs is designed to work in harmony with the others. A sale
              updates stock, creates a journal, and generates an invoice — automatically.
            </p>
          </div>
          <div class="platform-grid">
            <div v-for="mod in platformModules" :key="mod.title" class="platform-card">
              <div class="platform-icon" :style="{ background: mod.bg, color: mod.color }">
                <font-awesome-icon :icon="mod.icon" />
              </div>
              <div class="platform-info">
                <div class="platform-title">{{ mod.title }}</div>
                <div class="platform-desc">{{ mod.desc }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Our Values                                                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad">
        <div class="page-container">
          <div class="section-header">
            <p class="section-eyebrow">What We Stand For</p>
            <h2 class="section-title">Our Core Values</h2>
            <p class="section-body">The principles that guide every product decision we make and every feature we build.</p>
          </div>
          <div class="values-grid">
            <div v-for="val in values" :key="val.title" class="value-card">
              <div class="value-icon-wrap" :style="{ background: val.bg }">
                <font-awesome-icon :icon="val.icon" :style="{ color: val.color }" />
              </div>
              <h5 class="value-title">{{ val.title }}</h5>
              <p class="value-desc">{{ val.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Key Features (API-driven)                                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray">
        <div class="page-container">
          <div class="section-header">
            <span class="section-badge">Platform Features</span>
            <h2 class="section-title">Built to Handle the Complexity of Real Business</h2>
            <p class="section-body">From small traders to growing enterprises — Mapiibs has the tools to match.</p>
          </div>

          <div v-if="loading" class="loading-state">
            <div class="spinner-border text-primary" role="status" style="width:2.5rem;height:2.5rem;"></div>
          </div>

          <div v-else-if="features.length" class="features-grid">
            <div v-for="(feat, i) in features" :key="feat.id" class="feature-card">
              <div class="feature-icon-wrap" :style="{ background: featureColor(i).bg, color: featureColor(i).color }">
                <font-awesome-icon :icon="featureIcon(i)" />
              </div>
              <div class="feature-body">
                <h6 class="feature-title">{{ feat.title }}</h6>
                <p class="feature-desc">{{ feat.description }}</p>
              </div>
            </div>
          </div>

          <!-- Fallback static features if API returns nothing -->
          <div v-else class="features-grid">
            <div v-for="feat in staticFeatures" :key="feat.title" class="feature-card">
              <div class="feature-icon-wrap" :style="{ background: feat.bg, color: feat.color }">
                <font-awesome-icon :icon="feat.icon" />
              </div>
              <div class="feature-body">
                <h6 class="feature-title">{{ feat.title }}</h6>
                <p class="feature-desc">{{ feat.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Key Benefits (API-driven)                                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section v-if="benefits.length" class="section-pad">
        <div class="page-container">
          <div class="section-header">
            <h2 class="section-title">Why Businesses Choose Mapiibs</h2>
            <p class="section-body">Designed with real-world business challenges in mind — here's what sets us apart.</p>
          </div>
          <div class="benefits-grid">
            <div v-for="(benefit, i) in benefits" :key="benefit.id" class="benefit-card">
              <div class="benefit-number">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <h6 class="benefit-title">{{ benefit.title }}</h6>
                <p class="benefit-desc">{{ benefit.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- How it works                                                  -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="section-pad section-gray">
        <div class="page-container">
          <div class="section-header">
            <p class="section-eyebrow">Getting Started</p>
            <h2 class="section-title">Up and Running in Minutes</h2>
            <p class="section-body">No technical expertise required. Mapiibs is designed to be intuitive from day one.</p>
          </div>
          <div class="steps-grid">
            <div v-for="step in steps" :key="step.number" class="step-card">
              <div class="step-number">{{ step.number }}</div>
              <div class="step-icon-wrap" :style="{ background: step.bg, color: step.color }">
                <font-awesome-icon :icon="step.icon" />
              </div>
              <h5 class="step-title">{{ step.title }}</h5>
              <p class="step-desc">{{ step.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- CTA band                                                      -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <section class="cta-band">
        <div class="page-container cta-band-inner">
          <div>
            <h2 class="cta-title">Ready to Transform the Way You Run Your Business?</h2>
            <p class="cta-sub">Join businesses already using Mapiibs to automate, control, and grow.</p>
          </div>
          <div class="cta-actions">
            <router-link to="/auth/register" class="btn-white-lg">
              Get Started Free <font-awesome-icon icon="arrow-right" class="ms-2" />
            </router-link>
            <a href="/home#demo" class="btn-ghost-lg">Book a Demo</a>
          </div>
        </div>
      </section>

    </main>

    <Footer />
  </div>
</template>

<script>
import Navbar from '../components/Navbar';
import Footer from '../components/footer';
import axios  from 'axios';

export default {
  name: 'AboutPage',

  components: { Navbar, Footer },

  data() {
    return {
      details:  {},
      features: [],
      benefits: [],
      loading:  true,

      // ── Stats ────────────────────────────────────────────────────────
      stats: [
        { icon: 'th-large',      number: '13+',         label: 'Integrated Modules',    color: '#004AAD', bg: 'rgba(0,74,173,.1)'   },
        { icon: 'map-marker-alt', number: 'Multi-Site', label: 'Warehouse & Branch Support', color: '#16a34a', bg: 'rgba(22,163,74,.1)' },
        { icon: 'coins',         number: 'Multi-Currency', label: 'Global-Ready Operations', color: '#d97706', bg: 'rgba(217,119,6,.1)' },
        { icon: 'shield-alt',    number: '100%',        label: 'Secure & Encrypted',    color: '#7c3aed', bg: 'rgba(124,58,237,.1)' },
        { icon: 'clock',         number: '24/7',        label: 'Cloud Access, Anywhere', color: '#0d9488', bg: 'rgba(13,148,136,.1)' },
        { icon: 'chart-line',    number: 'Real-Time',   label: 'Reporting & Analytics', color: '#dc2626', bg: 'rgba(220,38,38,.1)'  },
      ],

      // ── Platform modules ─────────────────────────────────────────────
      platformModules: [
        { icon: 'boxes',           color: '#004AAD', bg: 'rgba(0,74,173,.1)',    title: 'Inventory Management',  desc: 'Multi-location stock control, barcodes, expiry & transfer management.' },
        { icon: 'shopping-cart',   color: '#16a34a', bg: 'rgba(22,163,74,.1)',   title: 'Sales Management',      desc: 'Sales orders, POS, credit sales and transaction reversals.' },
        { icon: 'truck',           color: '#ea580c', bg: 'rgba(234,88,12,.1)',   title: 'Purchase Management',   desc: 'Purchase orders, GRN, supplier tracking and cost management.' },
        { icon: 'file-invoice',    color: '#7c3aed', bg: 'rgba(124,58,237,.1)', title: 'Invoicing',             desc: 'Branded invoice generation, delivery, payment tracking and history.' },
        { icon: 'credit-card',     color: '#0d9488', bg: 'rgba(13,148,136,.1)', title: 'Billing',               desc: 'Billing cycles, payment reconciliation and client statements.' },
        { icon: 'calculator',      color: '#2563eb', bg: 'rgba(37,99,235,.1)',   title: 'Accounting',            desc: 'Double-entry journals, chart of accounts, P&L and balance sheet.' },
        { icon: 'users',           color: '#dc2626', bg: 'rgba(220,38,38,.1)',   title: 'HR Management',         desc: 'Employee records, departments, leave, attendance and access control.' },
        { icon: 'coins',           color: '#d97706', bg: 'rgba(217,119,6,.1)',   title: 'Payroll',               desc: 'Automated payroll runs, custom deductions and digital payslips.' },
        { icon: 'hand-holding-usd', color: '#059669', bg: 'rgba(5,150,105,.1)', title: 'Loan Management',       desc: 'Employee loans, approvals and payroll-integrated repayments.' },
        { icon: 'address-card',    color: '#be123c', bg: 'rgba(190,18,60,.1)',   title: 'Debtors',               desc: 'Credit sales tracking, outstanding balances and customer statements.' },
        { icon: 'landmark',        color: '#0891b2', bg: 'rgba(8,145,178,.1)',   title: 'Creditors',             desc: 'Supplier payables, payment scheduling and supplier reconciliation.' },
        { icon: 'chart-bar',       color: '#475569', bg: 'rgba(71,85,105,.1)',   title: 'Reports & Analytics',   desc: 'Sales, inventory, financial and HR reports with custom date ranges.' },
        { icon: 'cog',             color: '#6d28d9', bg: 'rgba(109,40,217,.1)',  title: 'Admin & Settings',      desc: 'Company setup, multi-user roles, permissions and audit logs.' },
      ],

      // ── Values ───────────────────────────────────────────────────────
      values: [
        {
          icon: 'project-diagram', color: '#004AAD', bg: 'rgba(0,74,173,.1)',
          title: 'Integration First',
          desc: 'Every module is built to connect seamlessly with the others. No data silos, no manual reconciliation between disconnected tools.',
        },
        {
          icon: 'shield-alt', color: '#16a34a', bg: 'rgba(22,163,74,.1)',
          title: 'Security by Design',
          desc: 'Role-based access control, end-to-end encryption and full audit logs ensure your business data stays safe and compliant.',
        },
        {
          icon: 'chart-line', color: '#7c3aed', bg: 'rgba(124,58,237,.1)',
          title: 'Built to Scale',
          desc: 'From a single-location SME to a multi-branch enterprise — Mapiibs grows with you without requiring re-implementation.',
        },
        {
          icon: 'lightbulb', color: '#d97706', bg: 'rgba(217,119,6,.1)',
          title: 'Simplicity at Scale',
          desc: 'Powerful features should not mean complicated workflows. We obsess over usability so your team can be productive from day one.',
        },
      ],

      // ── Steps ────────────────────────────────────────────────────────
      steps: [
        {
          number: '01', icon: 'user-plus', color: '#004AAD', bg: 'rgba(0,74,173,.1)',
          title: 'Create Your Account',
          desc: 'Sign up in seconds. No credit card required for your trial — just your name, email, and company name.',
        },
        {
          number: '02', icon: 'building', color: '#16a34a', bg: 'rgba(22,163,74,.1)',
          title: 'Set Up Your Company',
          desc: 'Configure your business details, add your team, define roles, and set up your chart of accounts and inventory.',
        },
        {
          number: '03', icon: 'sliders-h', color: '#7c3aed', bg: 'rgba(124,58,237,.1)',
          title: 'Activate Your Modules',
          desc: 'Enable only the modules your business needs. Each module is ready to use immediately — no complex configuration.',
        },
        {
          number: '04', icon: 'chart-line', color: '#d97706', bg: 'rgba(217,119,6,.1)',
          title: 'Run and Grow',
          desc: 'Process sales, manage stock, run payroll, and generate reports — all from one unified dashboard.',
        },
      ],

      // ── Static feature fallback ──────────────────────────────────────
      staticFeatures: [
        { icon: 'boxes',          color: '#004AAD', bg: 'rgba(0,74,173,.1)',    title: 'Real-Time Inventory',       desc: 'Live stock visibility across all locations with barcodes, batch and expiry tracking.' },
        { icon: 'calculator',     color: '#2563eb', bg: 'rgba(37,99,235,.1)',   title: 'Double-Entry Accounting',   desc: 'Automated journals, chart of accounts, and full financial statement generation.' },
        { icon: 'coins',          color: '#d97706', bg: 'rgba(217,119,6,.1)',   title: 'Automated Payroll',         desc: 'Configurable payroll runs with earnings, deductions, and digital payslip delivery.' },
        { icon: 'file-invoice',   color: '#7c3aed', bg: 'rgba(124,58,237,.1)', title: 'Professional Invoicing',    desc: 'Branded invoices with payment tracking, history, and automated reminders.' },
        { icon: 'chart-bar',      color: '#475569', bg: 'rgba(71,85,105,.1)',   title: 'Comprehensive Reporting',   desc: 'Cross-module reports from sales summaries to full financial dashboards.' },
        { icon: 'shield-alt',     color: '#16a34a', bg: 'rgba(22,163,74,.1)',   title: 'Enterprise Security',       desc: 'Role-based access, field-level permissions, and full audit logs on every action.' },
        { icon: 'map-marker-alt', color: '#dc2626', bg: 'rgba(220,38,38,.1)',   title: 'Multi-Location Support',    desc: 'Manage multiple warehouses, branches, and locations from a single dashboard.' },
        { icon: 'hand-holding-usd', color: '#059669', bg: 'rgba(5,150,105,.1)', title: 'Employee Loan Management', desc: 'Loan requests, approvals, repayment schedules and payroll-linked deductions.' },
      ],
    };
  },

  methods: {
    pageData() {
      axios.get('/admin/fetchProjectSetup')
        .then(({ data }) => { this.details = data.data; })
        .catch(() => {});
    },

    fetchFeatures() {
      axios.get('/admin/active-features')
        .then(({ data }) => {
          this.features = data.data.features ?? [];
          this.benefits = data.data.benefits ?? [];
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    featureIcon(index) {
      const icons = ['boxes', 'calculator', 'users', 'file-invoice', 'chart-bar', 'shield-alt', 'coins', 'truck', 'credit-card', 'landmark', 'chart-line', 'cog'];
      return icons[index % icons.length];
    },

    featureColor(index) {
      const palette = [
        { color: '#004AAD', bg: 'rgba(0,74,173,.1)'   },
        { color: '#2563eb', bg: 'rgba(37,99,235,.1)'  },
        { color: '#dc2626', bg: 'rgba(220,38,38,.1)'  },
        { color: '#7c3aed', bg: 'rgba(124,58,237,.1)' },
        { color: '#475569', bg: 'rgba(71,85,105,.1)'  },
        { color: '#16a34a', bg: 'rgba(22,163,74,.1)'  },
        { color: '#d97706', bg: 'rgba(217,119,6,.1)'  },
        { color: '#ea580c', bg: 'rgba(234,88,12,.1)'  },
        { color: '#0d9488', bg: 'rgba(13,148,136,.1)' },
        { color: '#0891b2', bg: 'rgba(8,145,178,.1)'  },
        { color: '#059669', bg: 'rgba(5,150,105,.1)'  },
        { color: '#6d28d9', bg: 'rgba(109,40,217,.1)' },
      ];
      return palette[index % palette.length];
    },
  },

  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.pageData();
    this.fetchFeatures();
  },
};
</script>

<style scoped>
/* ── Shared ──────────────────────────────────────────────────────────── */
.page-container { max-width:1160px; margin:0 auto; padding:0 24px; }
.section-pad    { padding:80px 0; }
.section-gray   { background:#f4f7fb; }
.accent         { color:#004AAD; }
.ms-2           { margin-left:8px; }

.section-header  { text-align:center; max-width:700px; margin:0 auto 52px; }
.section-badge   { display:inline-block; background:rgba(0,74,173,.1); color:#004AAD; border:1px solid rgba(0,74,173,.2); border-radius:20px; padding:4px 14px; font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.08em; margin-bottom:14px; }
.section-eyebrow { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#004AAD; margin:0 0 12px; }
.section-title   { font-size:clamp(22px,3.5vw,34px); font-weight:700; color:#1a2b45; margin:0 0 14px; line-height:1.25; }
.section-body    { font-size:15px; color:#6b7a99; line-height:1.7; margin:0; }

/* ── Page Hero ───────────────────────────────────────────────────────── */
.page-hero {
  position: relative;
  background: linear-gradient(135deg, #051B35 0%, #004AAD 100%);
  padding: 110px 0 72px;
  overflow: hidden;
}
.page-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url('/static/img/bg-1.jpg') center/cover no-repeat;
  opacity: 0.08;
}
.page-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.92) 0%, rgba(0,74,173,.75) 100%);
}
.page-hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 760px;
  margin: 0 auto;
}
.hero-eyebrow {
  display: inline-block;
  background: rgba(255,255,255,.12);
  color: rgba(255,255,255,.9);
  border: 1px solid rgba(255,255,255,.2);
  border-radius: 20px;
  padding: 5px 16px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .1em;
  margin-bottom: 20px;
}
.page-hero-title {
  font-size: clamp(32px, 6vw, 52px);
  font-weight: 800;
  color: #fff;
  margin: 0 0 18px;
  letter-spacing: -.01em;
  line-height: 1.15;
}
.page-hero-sub {
  font-size: 16px;
  color: rgba(255,255,255,.75);
  line-height: 1.7;
  margin: 0 auto 28px;
  max-width: 580px;
}

/* ── Breadcrumb ──────────────────────────────────────────────────────── */
.breadcrumb-nav     { display:inline-flex; align-items:center; gap:8px; font-size:14px; }
.breadcrumb-link    { color:rgba(255,255,255,.6); text-decoration:none; transition:color .15s; }
.breadcrumb-link:hover { color:#fff; }
.breadcrumb-sep     { color:rgba(255,255,255,.3); }
.breadcrumb-current { color:rgba(255,255,255,.45); }

/* ── Mission strip ───────────────────────────────────────────────────── */
.mission-strip { background:linear-gradient(135deg,#edf4ff,#f0fdf4); border-top:1px solid #c7d9f7; border-bottom:1px solid #c7d9f7; padding:28px 0; }
.mission-inner { display:flex; align-items:flex-start; gap:18px; max-width:860px; margin:0 auto; }
.mission-icon  { font-size:28px; color:#d97706; flex-shrink:0; margin-top:2px; }
.mission-text  { font-size:16px; color:#1a2b45; font-weight:500; line-height:1.7; margin:0; font-style:italic; }

/* ── Split grid ──────────────────────────────────────────────────────── */
.split-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
.split-content { display:flex; flex-direction:column; gap:20px; }
.split-visual img { width:100%; max-width:480px; }

/* ── Check list ──────────────────────────────────────────────────────── */
.check-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; }
.check-list li { display:flex; align-items:center; gap:10px; font-size:14px; font-weight:500; color:#1a2b45; }
.check-icon { color:#16a34a; font-size:14px; flex-shrink:0; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-primary-md { display:inline-flex; align-items:center; gap:8px; background:#004AAD; color:#fff; font-size:14px; font-weight:600; padding:10px 22px; border-radius:9px; text-decoration:none; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-md:hover { background:#003b8e; color:#fff; text-decoration:none; }
.btn-white-lg { display:inline-flex; align-items:center; gap:8px; background:#fff; color:#004AAD; font-size:14px; font-weight:700; padding:12px 26px; border-radius:10px; text-decoration:none; border:none; cursor:pointer; transition:all .15s; white-space:nowrap; }
.btn-white-lg:hover { background:#e8f0fb; text-decoration:none; }
.btn-ghost-lg { display:inline-flex; align-items:center; gap:8px; background:rgba(255,255,255,.12); color:#fff; border:1px solid rgba(255,255,255,.3); font-size:14px; font-weight:600; padding:12px 26px; border-radius:10px; text-decoration:none; cursor:pointer; transition:all .15s; white-space:nowrap; }
.btn-ghost-lg:hover { background:rgba(255,255,255,.2); text-decoration:none; }

/* ── Stats section ───────────────────────────────────────────────────── */
.stats-section { background:#fff; border-top:1px solid #e4eaf2; border-bottom:1px solid #e4eaf2; padding:48px 0; }
.stats-grid    { display:grid; grid-template-columns:repeat(6,1fr); gap:20px; }
.stat-card     { display:flex; flex-direction:column; align-items:center; text-align:center; gap:10px; }
.stat-icon-wrap { width:52px; height:52px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.stat-number   { font-size:22px; font-weight:800; color:#1a2b45; line-height:1; }
.stat-label    { font-size:12px; color:#7a90b0; font-weight:600; text-align:center; line-height:1.4; }

/* ── Platform grid ───────────────────────────────────────────────────── */
.platform-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(260px, 1fr)); gap:16px; }
.platform-card {
  display:flex; align-items:flex-start; gap:14px;
  background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px;
  transition:box-shadow .2s, border-color .2s, transform .2s;
}
.platform-card:hover { box-shadow:0 8px 24px rgba(0,0,0,.08); border-color:#c5d6f5; transform:translateY(-2px); }
.platform-icon  { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.platform-title { font-size:13.5px; font-weight:700; color:#1a2b45; margin-bottom:4px; }
.platform-desc  { font-size:12.5px; color:#6b7a99; line-height:1.5; margin:0; }

/* ── Values grid ─────────────────────────────────────────────────────── */
.values-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
.value-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:28px 24px; text-align:center; transition:box-shadow .2s, transform .2s; }
.value-card:hover { box-shadow:0 10px 30px rgba(0,0,0,.09); transform:translateY(-3px); }
.value-icon-wrap { width:56px; height:56px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:22px; margin:0 auto 16px; }
.value-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 10px; }
.value-desc  { font-size:13.5px; color:#6b7a99; line-height:1.65; margin:0; }

/* ── Features grid ───────────────────────────────────────────────────── */
.features-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(280px, 1fr)); gap:20px; }
.feature-card  { display:flex; align-items:flex-start; gap:16px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:22px; transition:box-shadow .2s, transform .2s; }
.feature-card:hover { box-shadow:0 8px 24px rgba(0,0,0,.08); transform:translateY(-2px); }
.feature-icon-wrap { width:44px; height:44px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.feature-body  { flex:1; }
.feature-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 7px; }
.feature-desc  { font-size:13.5px; color:#6b7a99; line-height:1.6; margin:0; }

/* ── Benefits grid ───────────────────────────────────────────────────── */
.benefits-grid  { display:grid; grid-template-columns:repeat(2,1fr); gap:20px; }
.benefit-card   { display:flex; align-items:flex-start; gap:20px; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; transition:box-shadow .2s; }
.benefit-card:hover { box-shadow:0 8px 24px rgba(0,0,0,.07); }
.benefit-number { font-size:32px; font-weight:900; color:#e4eaf2; line-height:1; flex-shrink:0; min-width:48px; }
.benefit-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.benefit-desc   { font-size:13.5px; color:#6b7a99; line-height:1.65; margin:0; }

/* ── Steps grid ──────────────────────────────────────────────────────── */
.steps-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:24px; position:relative; }
.steps-grid::before {
  content:'';
  position:absolute;
  top:52px; left:12%;
  width:76%; height:2px;
  background:linear-gradient(90deg,#004AAD,#16a34a);
  z-index:0;
}
.step-card  { display:flex; flex-direction:column; align-items:center; text-align:center; gap:12px; position:relative; z-index:1; background:#f4f7fb; border-radius:14px; padding:28px 20px; }
.step-number { font-size:12px; font-weight:800; color:#fff; background:#004AAD; border-radius:20px; padding:3px 10px; letter-spacing:.05em; }
.step-icon-wrap { width:56px; height:56px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:22px; }
.step-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }
.step-desc  { font-size:13px; color:#6b7a99; line-height:1.6; margin:0; }

/* ── Loading state ───────────────────────────────────────────────────── */
.loading-state { display:flex; justify-content:center; padding:48px 0; }

/* ── CTA band ────────────────────────────────────────────────────────── */
.cta-band       { background:linear-gradient(135deg,#004AAD,#0059d6); padding:64px 0; }
.cta-band-inner { display:flex; align-items:center; justify-content:space-between; gap:24px; flex-wrap:wrap; }
.cta-title      { font-size:clamp(20px,3vw,28px); font-weight:700; color:#fff; margin:0 0 8px; }
.cta-sub        { font-size:15px; color:rgba(255,255,255,.75); margin:0; }
.cta-actions    { display:flex; align-items:center; gap:12px; flex-wrap:wrap; flex-shrink:0; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:1100px) {
  .stats-grid  { grid-template-columns:repeat(3,1fr); }
  .values-grid { grid-template-columns:repeat(2,1fr); }
  .steps-grid  { grid-template-columns:repeat(2,1fr); }
  .steps-grid::before { display:none; }
}
@media (max-width:900px) {
  .features-grid  { grid-template-columns:1fr; }
  .benefits-grid  { grid-template-columns:1fr; }
  .platform-grid  { grid-template-columns:repeat(2,1fr); }
}
@media (max-width:768px) {
  .section-pad   { padding:56px 0; }
  .split-grid    { grid-template-columns:1fr; gap:32px; }
  .split-visual  { display:none; }
  .cta-band-inner { flex-direction:column; align-items:flex-start; }
  .cta-actions   { flex-direction:column; align-items:flex-start; }
  .page-hero     { padding:80px 0 52px; }
  .stats-grid    { grid-template-columns:repeat(2,1fr); }
  .values-grid   { grid-template-columns:1fr; }
  .steps-grid    { grid-template-columns:1fr; }
  .platform-grid { grid-template-columns:1fr; }
  .mission-inner { flex-direction:column; gap:12px; }
}
</style>
